import z from 'zod';
import { useEffect } from 'react';
import { SearchSchema } from '@anytag/shared-validations';
import { POST_COMMENTS_LIMIT } from '@anytag/shared-constants';
import { useParamsSearch, useQueryHelper } from '@anytag/shared-hooks';
import { checkIsReelPost, getValuableVariables } from '@anytag/shared-utils';
import { PostDetailStatistics, SupportedSocialMediaType } from '@anytag/shared-types';
import {
  Maybe,
  EngagementPostDetail,
  CampaignSocialMediaType,
  useEngagementPostDetailQuery,
  useEngagementPostCommentsQuery,
  useEngagementPostTrafficSourceQuery
} from '@anytag/graphql';
import { PostModal, usePostModalContext } from '../shared';
import { TrafficSourceChart } from './traffic-source-chart';
import { EngagementPostStatus } from './engagement-post-status';

const PeriodSearchSchema = SearchSchema.create({
  filter: {
    schema: z.object({
      endDate: SearchSchema.types.string().optional(),
      startDate: SearchSchema.types.string().optional()
    }),
    defaultValues: { endDate: undefined, startDate: undefined }
  }
});

export interface DetailsTemplateProps {
  hasTrafficSource?: boolean;
  footer?: (props: { postDetails?: Maybe<EngagementPostDetail> }) => React.ReactNode;
}

export const DetailsTemplate = ({ footer, hasTrafficSource }: DetailsTemplateProps) => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { closeModal, selectedPostId, setModalSize } = usePostModalContext();
  const { filter } = useParamsSearch<z.infer<typeof PeriodSearchSchema>>();
  const { data, loading } = useEngagementPostDetailQuery({
    skip: !selectedPostId,
    fetchPolicy: 'cache-and-network',
    variables: { pk: Number(selectedPostId) },
    onError: (error) => {
      closeModal?.();
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
  });
  const { data: commentsData, loading: fetchingComments } = useEngagementPostCommentsQuery({
    skip: !selectedPostId,
    fetchPolicy: 'cache-and-network',
    variables: { postId: Number(selectedPostId), limit: POST_COMMENTS_LIMIT }
  });
  const details = data?.engagementPostDetail;
  const { data: trafficSourceData, loading: trafficSourceLoading } = useEngagementPostTrafficSourceQuery({
    skip: !selectedPostId || !hasTrafficSource || details?.socialMedia !== CampaignSocialMediaType.YOUTUBE, // Only support EGG - YT post for now
    variables: getValuableVariables({
      pk: Number(selectedPostId),
      endDate: filter.endDate,
      startDate: filter.startDate
    })
  });

  const trafficSource = trafficSourceData?.engagementPostTrafficSource;

  useEffect(() => {
    setModalSize?.(trafficSource?.length ? 'xl' : null);
  }, [trafficSource?.length]);

  const statistics: PostDetailStatistics = (() => {
    switch (details?.socialMedia) {
      case CampaignSocialMediaType.INSTAGRAM:
        return { likes: details?.likes, reach: undefined, comments: details?.comments };
      case CampaignSocialMediaType.INSTAGRAM_STORY:
        return { likes: details?.likes, reach: undefined, replies: details?.comments };
      case CampaignSocialMediaType.YOUTUBE:
        return { likes: details?.likes, views: details?.views, comments: details?.comments };
      case CampaignSocialMediaType.TWITTER:
        return { likes: details?.likes, retweets: details?.shares, replies: details?.comments };
      case CampaignSocialMediaType.THREADS:
        return { likes: details?.likes, replies: details?.comments };
      case CampaignSocialMediaType.FACEBOOK:
        return { likes: details?.likes, comments: details?.comments, shares: details?.shares };
      case CampaignSocialMediaType.TIKTOK:
      case CampaignSocialMediaType.DOUYIN:
        return {
          likes: details?.likes,
          views: details?.views,
          comments: details?.comments,
          shares: details?.shares
        };
      case CampaignSocialMediaType.XHS:
        return {
          likes: details?.likes,
          views: details?.views,
          saves: details?.saves
        };
      default:
        return {};
    }
  })();

  return (
    <>
      <PostModal.DetailsTemplate
        loading={loading || fetchingComments || trafficSourceLoading}
        postStatus={
          details?.status ? (
            <EngagementPostStatus
              className="h-4 text-xs"
              status={details?.status}
              warningReason={details.warningReason || undefined}
            />
          ) : undefined
        }
        data={
          details
            ? {
                statistics,
                caption: details?.caption,
                postUrl: details?.postUrl,
                postDate: details?.postedDate,
                socialMedia: details?.socialMedia as unknown as SupportedSocialMediaType, // we don't support type OTHERS, UNSELECT, ...
                isReel: checkIsReelPost(details?.postUrl),
                comments: commentsData?.engagementPostComments || [],
                images: details?.images?.length ? details?.images : !details?.thumbNail ? [] : [details.thumbNail],
                info: {
                  id: String(details?.engagementPostDetailInfluencer?.id ?? ''),
                  name: details?.engagementPostDetailInfluencer?.name,
                  avatar: details?.engagementPostDetailInfluencer?.avatar,
                  followers: details?.engagementPostDetailInfluencer?.followersCount
                }
              }
            : undefined
        }
      >
        {trafficSource?.length ? (
          <>
            <h3 className="my-4 text-base font-semibold">{t('Traffic Source')}</h3>
            <TrafficSourceChart data={trafficSource} />
          </>
        ) : null}
      </PostModal.DetailsTemplate>

      {typeof footer === 'function' ? footer({ postDetails: details }) : null}
    </>
  );
};
